<template>
	<el-dialog title="技能配置明细" center :visible.sync="dialogVisible" :close-on-click-modal="false" :close-on-press-escape="false" width="700px">
		<!-- 说明文字 -->
		<p class="tip-text">每项课程配置，学员考试成绩需同时满足</p>
		<p class="tip-text">如有多项课程配置，学员考试成绩满足其中一组即可</p>

		<!-- 配置列表 -->
		<div class="config-list">
			<div v-for="(item, index) in configList" :key="index" class="config-item">
				<el-form label-position="left" :model="item" :ref="'formRef' + index" label-width="80px">
					<el-form-item :label="`课程配置${index + 1}`">
						<!-- 课程选择器：支持多选、远程搜索 -->
						<el-select
							v-model="item.courseId"
							multiple
							filterable
							remote
							reserve-keyword
							placeholder="请输入课程名称搜索"
							:remote-method="(query) => handleSearchCourse(query, index)"
							:loading="item.searchLoading"
							@focus="handleSelectFocus(index)">
							<el-option
								v-for="course in item.courseOptions"
								:key="course.id"
								:label="`${course.name}(${course.code || course.courseCode})`"
								:value="course.id">
								<span>{{ `${course.name}(${course.code || course.courseCode})` }}</span>
								<span v-if="course.externalCert == 1" class="external-cert">*</span>
							</el-option>
						</el-select>
						<!-- 删除按钮：只有超过1个配置项时才显示删除按钮 -->
						<el-button v-if="configList.length > 1" type="danger" size="small" @click="handleRemoveConfig(index)">删除</el-button>
					</el-form-item>
				</el-form>
			</div>
		</div>

		<!-- 新增按钮：最多4个配置项 -->
		<el-button type="primary" icon="el-icon-plus" size="mini" @click="handleAddConfig" :disabled="configList.length >= 7 || submitLoading">
			新增课程配置
		</el-button>

		<!-- 弹窗底部按钮 -->
		<span slot="footer" class="dialog-footer">
			<el-button @click="handleClose" :disabled="submitLoading">取 消</el-button>
			<el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
		</span>
	</el-dialog>
</template>

<script>
/**
 * 技能配置明细弹窗组件
 * 功能：管理课程配置的增删改查
 * 特性：支持多个课程配置、远程搜索、防重复提交
 */
import { debounce } from "@/utils/debounce";
import { courseInfoPageList, courseSkillBundleConfigDetailAdd, courseSkillBundleConfigDetailGet, courseSkillBundleConfigDetailUpdate } from "@/api/skill";

export default {
	name: "SkillConfigDetailDialog",

	data() {
		return {
			dialogVisible: false, // 弹窗显示状态
			courseList: [], // 课程列表缓存
			configList: [], // 配置列表，每项包含：courseId(已选课程ID数组)、courseOptions(下拉选项)、searchLoading(搜索加载状态)
			bundleId: "", // 配置包ID
			skillId: "", // 技能ID
			groupId: "", // 配置组ID（编辑时有值，新增时为空）
			submitLoading: false, // 提交按钮loading，防止重复提交
		};
	},

	methods: {
		/**
		 * 初始化弹窗
		 * @param {string} bundleId - 配置包ID
		 * @param {string} skillId - 技能ID
		 * @param {string} groupId - 配置组ID（有值表示编辑模式，空表示新增模式）
		 */
		init(bundleId, skillId, groupId) {
			this.dialogVisible = true;
			this.bundleId = bundleId;
			this.skillId = skillId;
			this.groupId = groupId;
			this.configList = [];

			// 加载课程列表数据
			if (groupId) {
				// 编辑模式：加载已有配置详情 + 课程列表
				this.getCourseInfo(groupId);
				this.getCourseInfoPageList(groupId);
			} else {
				// 新增模式：只加载课程列表
				this.getCourseInfoPageList("");
			}
		},

		/**
		 * 关闭弹窗
		 * 重置所有状态，确保下次打开是干净的状态
		 */
		handleClose() {
			this.dialogVisible = false;
			this.submitLoading = false;
		},

		/**
		 * 新增课程配置项
		 * 最多支持4个配置项
		 */
		handleAddConfig() {
			// 校验数量限制
			if (this.configList.length >= 7) {
				this.$message.warning("最多只能添加7个课程配置");
				return;
			}
			// 添加新的配置项，使用已缓存的课程列表初始化
			this.configList.push({
				courseId: "",
				courseOptions: this.courseList,
				searchLoading: false,
				minScore: 0,
				remark: "",
			});
		},

		/**
		 * 获取单个课程详细（编辑模式）
		 * 根据groupId获取已保存的配置详情
		 * @param {string} groupId - 配置组ID
		 */
		getCourseInfo(groupId) {
			courseSkillBundleConfigDetailGet({ groupId }).then((res) => {
				// 遍历每个配置组
				for (const element of res.data) {
					const courseId = [];
					// 提取课程ID并格式化数据
					for (const ele of element) {
						courseId.push(ele.courseId);
						// 统一字段名，方便下拉框使用
						ele.id = ele.courseId;
						ele.name = ele.courseName;
					}
					// 添加到配置列表
					this.configList.push({
						courseId: courseId,
						courseOptions: element,
						searchLoading: false,
						minScore: 0,
						remark: "",
					});
				}
			});
		},

		/**
		 * 获取课程列表（下拉选项）
		 * @param {string} groupId - 配置组ID（空表示新增模式）
		 */
		getCourseInfoPageList(groupId) {
			courseInfoPageList({
				pageNum: 1,
				pageSize: 10,
				searchKey: "",
			}).then((res) => {
				this.courseList = res.data.list || [];
				// 新增模式下，初始化一个空配置项
				if (groupId === "") {
					this.configList.push({
						courseId: "",
						courseOptions: this.courseList,
						searchLoading: false,
						minScore: 0,
						remark: "",
					});
				}
			});
		},

		/**
		 * 远程搜索课程
		 * @param {string} query - 搜索关键词
		 * @param {number} index - 配置项索引
		 */
		handleSearchCourse(query, index) {
			// 开启搜索loading状态
			this.configList[index].searchLoading = true;
			courseInfoPageList({
				pageNum: 1,
				pageSize: 10,
				searchKey: query || "",
			}).then((res) => {
				this.configList[index].courseOptions = res.data.list || [];
				this.configList[index].searchLoading = false;
			});
		},

		/**
		 * 下拉框获取焦点时，默认加载课程列表
		 * @param {number} index - 配置项索引
		 */
		handleSelectFocus(index) {
			// 如果下拉选项为空，则加载默认列表
			if (this.configList[index].courseOptions.length === 0) {
				this.handleSearchCourse("", index);
			}
		},

		/**
		 * 删除配置项
		 * @param {number} index - 配置项索引
		 */
		handleRemoveConfig(index) {
			this.configList.splice(index, 1);
		},

		/**
		 * 提交表单
		 * 根据是否有groupId判断是新增还是编辑
		 * 使用debounce防抖，防止重复提交
		 */
		handleSubmit: debounce(function () {
			if (this.submitLoading) return;
			this.handleClose();
			// 构建课程详情列表（过滤掉未选择的配置项）
			const courseDetailList = this.configList.filter((item) => item.courseId.length > 0).map((item) => item.courseId);
			console.log(courseDetailList);
			console.log(this.configList);

			// 提交参数
			const params = {
				courseDetailList,
				bundleId: this.bundleId,
				skillId: this.skillId,
			};

			// 根据编辑/新增模式调用不同接口
			const apiMethod = this.groupId ? courseSkillBundleConfigDetailUpdate : courseSkillBundleConfigDetailAdd;

			// 编辑模式下添加groupId参数
			if (this.groupId) {
				params.groupId = this.groupId;
			}

			apiMethod(params)
				.then((res) => {
					this.$message.success(this.groupId ? "编辑成功" : "新增成功");
					this.$emit("refresh");
				})
				.finally(() => {
					// 确保loading状态被重置
					this.submitLoading = false;
				});
		}, 300),
	},
};
</script>

<style lang="scss" scoped>
/* 提示文字样式 */
.tip-text {
	margin: 0;
	color: #909399;
	font-size: 12px;
	line-height: 1.6;
}

/* 配置列表容器 */
.config-list {
	margin-bottom: 20px;
	max-height: 400px;
	overflow-y: auto;
}

/* 表单项样式 */
.el-form {
	margin-top: 20px;

	// 下拉框宽度（留出删除按钮空间）
	.el-select {
		width: calc(100% - 90px);
		margin-right: 10px;
	}
}

.external-cert {
	color: red;
}
</style>
